import { reactive, toRefs } from "vue";
import { auth, db } from "@/firebaseConfig";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import router from "@/router";

const ORGANIZATION_EMAIL_DOMAIN = "ptu.ac.th";

export const authStore = reactive({
  // User states
  user: null,
  firebaseUser: null,
  authInitialized: false,

  // Status flags
  isLoadingUser: false,
  isAuthenticated: false,

  // UI states
  snackbar: false,
  snackbarMessage: "",
  snackbarColor: "success",
  drawer: false,
  rail: false,

  // Admin state
  isAdmin: false,
});

// Store the unsubscribe function
let unsubscribeAuth = null;

// Helper function to create consistent user object
function createUserObject(firebaseUser, firestoreData = {}) {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    name:
      firebaseUser.displayName ||
      firestoreData.name ||
      firebaseUser.email?.split("@")[0],
    photoURL: firebaseUser.photoURL || firestoreData.photoURL,
    role: firestoreData.role || "guest",
    department: firestoreData.department || "general",
    createdAt: firestoreData.createdAt,
    lastLogin: firestoreData.lastLogin,
  };
}

// Initialize auth state listener
export const initializeAuth = () => {
  return new Promise((resolve) => {
    // Catch any errors from redirect login (like in Line browser)
    getRedirectResult(auth).catch((error) => {
      console.error("Redirect auth error:", error);
      showSnackbar("การเข้าสู่ระบบขัดข้อง: " + error.message, "error");
    });

    unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      authStore.firebaseUser = firebaseUser;
      authStore.isLoadingUser = true;

      try {
        if (firebaseUser) {
          await processAuthenticatedUser(firebaseUser);
        } else {
          clearUserState();
        }
      } catch (error) {
        console.error("Auth processing error:", error);
        clearUserState();
        showSnackbar("เกิดข้อผิดพลาดในการยืนยันตัวตน", "error");
      } finally {
        authStore.isAuthenticated = !!authStore.user;
        authStore.isLoadingUser = false;
        authStore.authInitialized = true;
        resolve();
      }
    });
  });
};

async function processAuthenticatedUser(firebaseUser) {
  const userDocRef = doc(db, "users", firebaseUser.uid);
  const userDocSnap = await getDoc(userDocRef);

  let userData = {};
  let userRole = "guest";

  if (userDocSnap.exists()) {
    // Existing user
    userData = userDocSnap.data();
    userRole = userData.role || "guest";

    // Update last login
    await updateDoc(userDocRef, {
      lastLogin: new Date(),
    });
  } else {
    // New user
    const isOrgMember = firebaseUser.email?.endsWith(
      `@${ORGANIZATION_EMAIL_DOMAIN}`
    );
    userRole = isOrgMember ? "member" : "guest";

    userData = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName || firebaseUser.email?.split("@")[0],
      photoURL: firebaseUser.photoURL,
      role: userRole,
      createdAt: new Date(),
      lastLogin: new Date(),
      department: isOrgMember
        ? firebaseUser.email.split("@")[0].split(".")[0] || "general"
        : "external",
    };

    await setDoc(userDocRef, userData);
  }

  // Set user state
  authStore.user = createUserObject(firebaseUser, userData);
  authStore.isAdmin = userRole === "admin";
}

function clearUserState() {
  authStore.user = null;
  authStore.isAdmin = false;
}

function showSnackbar(message, color = "success") {
  authStore.snackbarMessage = message;
  authStore.snackbarColor = color;
  authStore.snackbar = true;
}

export function useAuth() {
  const login = async () => {
    try {

      authStore.isLoadingUser = true;
      const provider = new GoogleAuthProvider();

      provider.setCustomParameters({
        hd: ORGANIZATION_EMAIL_DOMAIN,
        prompt: "select_account",
      });


      const isLineBrowser = navigator.userAgent.includes("Line") || navigator.userAgent.includes("LINE");
      let result;

      if (isLineBrowser) {
        await signInWithRedirect(auth, provider);
        return; // Page will redirect, execution stops here
      } else {
        result = await signInWithPopup(auth, provider);
      }

      // Wait for auth state to be processed before redirecting
      await new Promise((resolve) => {
        const checkAuth = () => {
          if (authStore.authInitialized && !authStore.isLoadingUser) {
            resolve();
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });

      if (authStore.isAuthenticated) {
        showSnackbar("เข้าสู่ระบบสำเร็จ!", "success");
        router.push("/dashboard");
        authStore.drawer = true;
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      console.error("❌ Login failed with error:", {
        code: error.code,
        message: error.message,
        stack: error.stack,
        fullError: error,
      });

      // Handle specific error cases
      let errorMessage = "เข้าสู่ระบบล้มเหลว";

      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "การเข้าสู่ระบบถูกยกเลิก";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage =
          "กรุณาอนุญาต popup เพื่อเข้าสู่ระบบ หรือปิด popup blocker";
      } else if (error.code === "auth/unauthorized-domain") {
        errorMessage = "โดเมนไม่ได้รับอนุญาต กรุณาตรวจสอบการตั้งค่า Firebase";
        console.error(
          "Domain not authorized. Check Firebase Console > Authentication > Settings > Authorized domains"
        );
      } else if (error.code === "auth/operation-not-allowed") {
        errorMessage = "การเข้าสู่ระบบด้วย Google ไม่ได้เปิดใช้งาน";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "พยายามเข้าสู่ระบบมากเกินไป กรุณารอสักครู่";
      } else if (error.message) {
        errorMessage += ": " + error.message;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      authStore.isLoadingUser = false;
    }
  };
  const logout = async () => {
    try {
      // Clear state first, then sign out
      clearUserState();
      authStore.isAuthenticated = false;

      await signOut(auth);

      showSnackbar("ออกจากระบบสำเร็จ!", "info");
      router.push("/");
      authStore.drawer = false;
      authStore.rail = false;
    } catch (error) {
      console.error("Logout error:", error);
      showSnackbar("ออกจากระบบล้มเหลว: " + error.message, "error");
    }
  };

  const refreshUser = async () => {
    if (!authStore.firebaseUser) {
      return;
    }

    try {
      // Refresh Firebase token
      await authStore.currentUser?.getIdToken(true);

      // Refresh user data from Firestore
      const userDocRef = doc(db, "users", authStore.firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        authStore.user = createUserObject(authStore.firebaseUser, userData);
        authStore.isAdmin = userData.role === "admin";
      } else {
      }
    } catch (error) {
      console.error("Refresh error:", error);
      showSnackbar("ไม่สามารถอัปเดตข้อมูลผู้ใช้ได้", "error");
    }
  };

  const checkAdmin = async () => {
    if (!authStore.firebaseUser) {
      return false;
    }

    try {
      const userDocRef = doc(db, "users", authStore.firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const isAdmin = userDocSnap.data().role === "admin";
        authStore.isAdmin = isAdmin;
        return isAdmin;
      }
    } catch (error) {
      console.error("Check admin error:", error);
    }

    return false;
  };

  const waitForAuthInit = async () => {
    if (authStore.authInitialized) return;

    return new Promise((resolve) => {
      const checkInit = () => {
        if (authStore.authInitialized) {
          resolve();
        } else {
          setTimeout(checkInit, 50);
        }
      };
      checkInit();
    });
  };

  return {
    ...toRefs(authStore),
    login,
    logout,
    refreshUser,
    checkAdmin,
    waitForAuthInit,
  };
}

// Cleanup function for when the app unmounts
export const cleanupAuth = () => {
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }
};
